// src/components/QuetzalShell.tsx
// QUETZAL — Inteligencia Turística Viva · México 2026
//
// Capa de inteligencia montada SOBRE el dashboard de agencias (TravelAdmin).
// Activa cuando hostname empieza con "quetzal" o querystring `?quetzal=1`
// o localStorage flag `quetzal_demo_mode='1'`. Default OFF para no romper
// las orgs de viajes que no son parte del demo CONCANACO.
//
// Componentes:
//   · QuetzalBanner   — header rebrand sobre el título original.
//   · quetzalRelabel  — helper para renombrar tab labels condicionalmente.
//   · QuetzalVoiceWidget — FAB que abre VoiceChat (OpenAI Realtime) en modal,
//     ya wired contra el backend Orion staging (multilingüe nativo).
//
// El WebSocket de voz vive en /orion/api/ws/voice/realtime — el mismo que
// usa el módulo /voice/voice del dashboard.

import React, { lazy, Suspense, useState } from 'react';
import { MessageCircle, X, Loader2, Globe } from 'lucide-react';

// Lazy-load VoiceChat para no inflar el bundle del TravelAdmin cuando
// QUETZAL no está activo (lazy chunk).
const VoiceChat = lazy(() => import('@/components/VoiceChat'));

const TAGLINE = 'Inteligencia Turística Viva · México 2026';
const ACCENT = '#0EA5A4'; // verde quetzal mesoamericano
const ACCENT_GOLD = '#F59E0B';

/** Activación: hostname comienza con "quetzal" o querystring `?quetzal=1`. */
export function isQuetzalMode(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  if (host.startsWith('quetzal')) return true;
  const params = new URLSearchParams(window.location.search);
  if (params.get('quetzal') === '1') {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('quetzal_demo_mode', '1');
    }
    return true;
  }
  if (typeof localStorage !== 'undefined' &&
      localStorage.getItem('quetzal_demo_mode') === '1') {
    return true;
  }
  return false;
}

/** Renombrado dinámico de labels cuando QUETZAL está activo. */
export function quetzalRelabel(originalLabel: string): string {
  if (!isQuetzalMode()) return originalLabel;
  const map: Record<string, string> = {
    'Viajes': 'Destinos turísticos',
    'Administrador de Viajes': 'QUETZAL · Inteligencia Turística',
    'Agencia de Viajes': 'Centro de Inteligencia Turística',
  };
  return map[originalLabel] ?? originalLabel;
}

/* ═══════════════════════════════════════════════════════════════
   Banner — se renderiza arriba del header de TravelAdmin
   ═══════════════════════════════════════════════════════════════ */

export function QuetzalBanner() {
  if (!isQuetzalMode()) return null;
  return (
    <div
      className="rounded-xl px-5 py-3 mb-4 flex items-center justify-between"
      style={{
        background: `linear-gradient(135deg, ${ACCENT}22 0%, ${ACCENT_GOLD}1a 100%)`,
        border: `1px solid ${ACCENT}55`,
      }}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            fontSize: 28, lineHeight: 1,
            filter: 'drop-shadow(0 0 6px rgba(14,165,164,0.4))',
          }}
        >
          🪶
        </span>
        <div>
          <div
            className="text-xl font-extrabold tracking-tight"
            style={{ color: ACCENT, letterSpacing: '0.02em' }}
          >
            QUETZAL
          </div>
          <div className="text-xs text-white/70">{TAGLINE}</div>
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-2 text-xs text-white/50">
        <Globe size={12} style={{ color: ACCENT_GOLD }} />
        <span>Voz multilingüe · ES · EN · FR · PT · 中文 · 日本語</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Voice widget — FAB esquina inferior derecha + modal full-screen
   con VoiceChat embebido (OpenAI Realtime, multilingüe).
   ═══════════════════════════════════════════════════════════════ */

export function QuetzalVoiceWidget() {
  if (!isQuetzalMode()) return null;
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label="Hablar con QUETZAL"
        className="fixed bottom-6 right-6 z-50 rounded-full shadow-2xl flex items-center gap-2 px-5 py-3 text-white font-semibold transition-transform hover:scale-105"
        style={{
          background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_GOLD} 100%)`,
          boxShadow: '0 10px 30px rgba(14,165,164,0.5)',
        }}
      >
        <MessageCircle size={18} />
        <span className="hidden sm:inline">Habla con QUETZAL</span>
        <span className="sm:hidden text-base">🪶</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
      style={{ background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="relative w-full max-w-5xl h-[min(90vh,800px)] rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(15, 23, 42, 0.97)',
          border: `1px solid ${ACCENT}55`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
        }}
      >
        {/* Header bar */}
        <div
          className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-2.5"
          style={{
            background: `linear-gradient(135deg, ${ACCENT}cc 0%, ${ACCENT_GOLD}cc 100%)`,
          }}
        >
          <div className="flex items-center gap-2">
            <span aria-hidden style={{ fontSize: 22 }}>🪶</span>
            <div>
              <div className="font-extrabold text-white text-base leading-tight">
                QUETZAL
              </div>
              <div className="text-[10px] text-white/85 leading-tight">
                Concierge multilingüe · Voz en tiempo real · México 2026
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Cerrar"
            className="text-white/95 hover:text-white p-1.5 rounded-md hover:bg-white/15"
          >
            <X size={20} />
          </button>
        </div>

        {/* VoiceChat embebido — el header transparente se monta por encima */}
        <div className="absolute inset-0 pt-12">
          <Suspense fallback={
            <div className="flex items-center justify-center h-full text-white/60">
              <Loader2 size={24} className="animate-spin mr-2" />
              Cargando QUETZAL voz…
            </div>
          }>
            <VoiceChat />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
